import { State } from '@course/components/module-helpers';
import { SprintWeek } from 'shared/sprint-week';

/**
 * How big a moment marking a lesson complete was, largest first:
 *
 * - `course`    every lesson in the course is now complete
 * - `week`      every lesson in this sprint cycle is now done
 * - `on_track`  nothing released is left undone (sprint courses only)
 * - `module`    the lesson's module is now complete (modules of 2+ lessons;
 *               a one-lesson module would celebrate every single click)
 * - `lesson`    the lesson alone
 */
export type CelebrationTier = NonNullable<State['celebration']>['tier'];

/** What marking a lesson complete produced, for the button that did it. */
export type LessonCompleteResult = {
  tier?: CelebrationTier;
  /** Every module opened early for this student so far, when a coach lets them work ahead. */
  unlockedModuleIds?: UUID[];
};

export function celebrationTier(opts: {
  lessonId: UUID;
  /** Completed lesson ids before the click. */
  completedBefore: UUID[];
  /** Every lesson a student can see, in course order. */
  courseLessonIds: UUID[];
  moduleLessonIds: UUID[];
  sprintBefore?: SprintWeek;
  sprintAfter?: SprintWeek;
}): CelebrationTier {
  const completedAfter = new Set([...opts.completedBefore, opts.lessonId]);
  const allDone = (ids: UUID[]) => ids.length > 0 && ids.every((id) => completedAfter.has(id));

  if (allDone(opts.courseLessonIds)) {
    return 'course';
  }

  const { sprintBefore, sprintAfter } = opts;
  if (sprintBefore && sprintAfter) {
    const weekDone = (week: SprintWeek) =>
      week.lessons.length > 0 && week.lessons.every((l) => l.status === 'done');
    if (weekDone(sprintAfter) && !weekDone(sprintBefore)) {
      return 'week';
    }
    if (sprintAfter.overdue.length === 0 && sprintBefore.overdue.length > 0) {
      return 'on_track';
    }
  }

  if (opts.moduleLessonIds.length > 1 && allDone(opts.moduleLessonIds)) {
    return 'module';
  }
  return 'lesson';
}
